import { Children, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown } from '../../theme/icons.js';
import './ChipRow.css';

/**
 * Eine Zeile Chips — was nicht hineinpasst, wandert in „Mehr".
 *
 * Die Regel der Website für Bedienleisten (DESIGN-SYSTEM.md §6, dort
 * js/filterleiste.js): Chips stehen in EINER Zeile; passt die Reihe nicht,
 * ersetzt ein Ausklappmenü, was übersteht. Vorher brachen die Filter auf dem
 * Schreibtisch in zwei Zeilen und liefen auf dem Telefon als wischbarer
 * Streifen ohne Hinweis über den Rand (Audit C4, #104).
 *
 * Die Chips bleiben, was sie sind: die Seite reicht ihre <button>-Elemente
 * als Kinder herein und behält Zustand und Klick-Logik. Diese Komponente
 * misst nur, wie viele in die Zeile passen, zeigt die vorderen dort und die
 * hinteren im Menü — dieselben Elemente, nur an anderer Stelle.
 *
 * Messen: eine unsichtbare Kopie aller Chips (visibility: hidden, Höhe 0)
 * liefert die Breiten; ein ResizeObserver rechnet bei jeder Breitenänderung
 * neu. Die Kopie hängt nicht im Zugänglichkeitsbaum und nimmt keinen Fokus.
 *
 * Aktiv-Zustand versteckter Chips: ein Chip gilt als aktiv, wenn er
 * aria-pressed="true" trägt. „Mehr" zeigt dann die Zahl — damit man sieht,
 * DASS gefiltert ist, auch wenn der Chip gerade nicht in der Zeile steht.
 */
export function ChipRow({ children, moreLabel = 'Mehr', ariaLabel = 'Weitere Filter', className = '' }) {
  const items = Children.toArray(children).filter(Boolean);
  const wrapRef = useRef(null);
  const lineRef = useRef(null);
  const measureRef = useRef(null);
  const moreRef = useRef(null);
  const popupRef = useRef(null);
  const [visible, setVisible] = useState(items.length);
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    const line = lineRef.current;
    const meas = measureRef.current;
    if (!line || !meas) return undefined;

    const compute = () => {
      const avail = line.clientWidth;
      const kids = Array.from(meas.children);
      if (!avail || !kids.length) return;
      const gap = parseFloat(getComputedStyle(meas).columnGap) || 0;
      const widths = kids.map((k) => k.offsetWidth);
      const all = widths.reduce((sum, w, i) => sum + w + (i ? gap : 0), 0);
      if (all <= avail) {
        setVisible(kids.length);
        // Dreht das Telefon quer und passt nun alles: das Menü schließt
        // sich mit, sein Platz ist dann die Zeile selbst.
        closeMenuRef.current();
        return;
      }
      // „Mehr" braucht selbst Platz — die eigene Breite, solange es noch
      // nicht steht, geschätzt.
      const moreW = (moreRef.current?.offsetWidth || 96) + gap;
      let used = 0;
      let fit = 0;
      for (let i = 0; i < widths.length; i++) {
        const next = used + (i ? gap : 0) + widths[i];
        if (next + moreW > avail) break;
        used = next;
        fit = i + 1;
      }
      setVisible(fit);
    };

    compute();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(compute);
    ro.observe(line);
    ro.observe(meas);
    return () => ro.disconnect();
    // visible steht in den Abhängigkeiten, damit nach dem ersten Erscheinen
    // von „Mehr" mit dessen echter Breite nachgerechnet wird.
  }, [items.length, visible]);

  // Saß die Tastatur im Menü, geht ihr Fokus zur „Mehr"-Taste zurück,
  // bevor es verschwindet - sonst landet sie auf dem body. (Audit P3)
  const closeMenu = () => {
    const popup = popupRef.current;
    if (popup && popup.contains(document.activeElement) && moreRef.current) {
      moreRef.current.focus();
    }
    setOpen(false);
  };
  const closeMenuRef = useRef(closeMenu);
  closeMenuRef.current = closeMenu;

  // Menü schließen: Klick außerhalb, Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) closeMenuRef.current();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') closeMenuRef.current();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const shown = items.slice(0, visible);
  const hidden = items.slice(visible);
  const hiddenActive = hidden.filter((el) => el.props?.['aria-pressed'] === true).length;

  const popupShown = open && hidden.length > 0;

  return (
    <div className={`chip-row ${className}`} ref={wrapRef}>
      <div className="chip-row-measure" ref={measureRef} aria-hidden="true">
        {items}
      </div>
      <div className="chip-row-line" ref={lineRef}>
        {shown}
        {hidden.length > 0 && (
          <button
            ref={moreRef}
            type="button"
            className={`stat-chip chip-more ${hiddenActive ? 'active' : ''}`}
            aria-haspopup="true"
            aria-expanded={open}
            aria-label={hiddenActive ? `${ariaLabel}, ${hiddenActive} aktiv` : ariaLabel}
            onClick={() => setOpen((o) => !o)}
          >
            {moreLabel}
            {hiddenActive > 0 && <span className="stat-chip-count">{hiddenActive}</span>}
            <ChevronDown size={14} aria-hidden="true" />
          </button>
        )}
      </div>
      {popupShown && (
        <div className="chip-more-popup" role="group" aria-label={ariaLabel} ref={popupRef}>
          {hidden}
        </div>
      )}
    </div>
  );
}

export default ChipRow;
